import { useTranslation } from 'react-i18next';
import {
  Box,
  DefaultBankAccountList,
  ElementContainer,
  TextField,
  Tooltip,
  BodyText,
  SupportedCurrencies,
  getMaskedNumber,
} from 'vopay';
import { useClientData } from '@/providers/ClientData.provider';

type PayoutAccountFieldProps = {
  currency: SupportedCurrencies;
  account: DefaultBankAccountList;
};

/**
 * Read-only: the API resolves the payout account as the currency's default operational bank account, and rejects a
 * request that sends banking details with it.
 */
export default function PayoutAccountField({ currency, account }: PayoutAccountFieldProps) {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Accounts.AutoBalanceTransfer.SetUpTransfer.PayoutAccountField',
  });

  const {
    response: { clientAccountsEnabled },
  } = useClientData();

  // Client-accounts users pay out to the primary client account, and have no Operational Accounts page to change it.
  const tooltip = clientAccountsEnabled
    ? t('tooltipClientAccount', {
        defaultValue:
          'The default {{currency}} bank account of your primary client account. Every transfer on this schedule lands here.',
        currency,
      })
    : t('tooltip', {
        defaultValue:
          'Your default {{currency}} operational account. Every transfer on this schedule lands here — change it under Bank Accounts.',
        currency,
      });

  const payoutAccount = [currency, account.AccountHolderName, getMaskedNumber(account.AccountNumber, '••••')];

  return (
    <ElementContainer
      label={
        <Box ai="center" gapSize="4px">
          {t('label', { defaultValue: 'Transfers To' })}

          <Tooltip
            type="hover"
            icon="info"
            position="right"
            hasArrow={false}
            information={<BodyText>{tooltip}</BodyText>}
          />
        </Box>
      }
      isBold={false}
      padding="0px 0px 8px"
      input={
        <TextField name="payoutAccount" variant="outlineShadow" value={payoutAccount.join(' | ')} disabled readOnly />
      }
    />
  );
}
